import type { TourAlign, TourSide } from './types';

export interface IRect { x: number; y: number; w: number; h: number }
export interface ISize { w: number; h: number }

const GAP = 18;      // bulb to hole
const MARGIN = 12;   // bulb to viewport edge
const INSET = 16;    // bulb inside a large hole

export function unionRect(rects: IRect[]): IRect {
  const x = Math.min(...rects.map((r) => r.x));
  const y = Math.min(...rects.map((r) => r.y));
  const right = Math.max(...rects.map((r) => r.x + r.w));
  const bottom = Math.max(...rects.map((r) => r.y + r.h));
  return { x, y, w: right - x, h: bottom - y };
}

export function padRect(r: IRect, pad: number): IRect {
  return { x: r.x - pad, y: r.y - pad, w: r.w + pad * 2, h: r.h + pad * 2 };
}

/** The sheet as one even-odd path: the viewport, minus a rounded hole. */
export function holePath(viewport: ISize, hole: IRect | null, radius = 10): string {
  const sheet = `M0 0H${viewport.w}V${viewport.h}H0Z`;
  if (!hole) return sheet;
  const { x, y, w, h } = hole;
  const r = radius;
  return (
    `${sheet} M${x + r} ${y}H${x + w - r}A${r} ${r} 0 0 1 ${x + w} ${y + r}` +
    `V${y + h - r}A${r} ${r} 0 0 1 ${x + w - r} ${y + h}H${x + r}` +
    `A${r} ${r} 0 0 1 ${x} ${y + h - r}V${y + r}A${r} ${r} 0 0 1 ${x + r} ${y}Z`
  );
}

export function placeModal(bulb: ISize, viewport: ISize): { left: number; top: number } {
  return {
    left: Math.round((viewport.w - bulb.w) / 2),
    top: Math.round((viewport.h - bulb.h) / 2 - 20),
  };
}

function along(size: number, start: number, length: number, align: TourAlign): number {
  if (align === 'start') return start;
  if (align === 'end') return start + length - size;
  return start + (length - size) / 2;
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/**
 * Where the bulb goes for one hole. The preferred side wins when it fits;
 * otherwise right, left, bottom, top, in that order; bottom when nothing fits.
 * `inside` is for a hole the bulb cannot sit beside — it takes the hole's
 * bottom corner, within whatever part of the hole is on screen.
 */
export function placeBulb(
  hole: IRect,
  bulb: ISize,
  viewport: ISize,
  pref: { side?: TourSide; align?: TourAlign },
): { left: number; top: number; side: TourSide; tail: number } {
  const align = pref.align ?? 'center';

  if (pref.side === 'inside') {
    const visibleRight = Math.min(hole.x + hole.w, viewport.w - MARGIN);
    const visibleTop = Math.max(hole.y, MARGIN);
    const visibleBottom = Math.min(hole.y + hole.h, viewport.h - MARGIN);
    const left = align === 'start' ? Math.max(hole.x, MARGIN) + INSET : visibleRight - bulb.w - INSET;
    const top = Math.max(visibleTop + INSET, visibleBottom - bulb.h - INSET);
    return { left: Math.round(left), top: Math.round(top), side: 'inside', tail: 0 };
  }

  const fits: Record<Exclude<TourSide, 'inside'>, boolean> = {
    right: hole.x + hole.w + GAP + bulb.w <= viewport.w - MARGIN,
    left: hole.x - GAP - bulb.w >= MARGIN,
    bottom: hole.y + hole.h + GAP + bulb.h <= viewport.h - MARGIN,
    top: hole.y - GAP - bulb.h >= MARGIN,
  };
  const order: Exclude<TourSide, 'inside'>[] = ['right', 'left', 'bottom', 'top'];
  const preferred = pref.side && fits[pref.side] ? pref.side : undefined;
  const side = preferred ?? order.find((s) => fits[s]) ?? 'bottom';

  if (side === 'right' || side === 'left') {
    const left = side === 'right' ? hole.x + hole.w + GAP : hole.x - GAP - bulb.w;
    const top = clamp(along(bulb.h, hole.y, hole.h, align), MARGIN, viewport.h - MARGIN - bulb.h);
    const tail = clamp(hole.y + Math.min(hole.h / 2, 60) - top - 8, 14, bulb.h - 30);
    return { left: Math.round(left), top: Math.round(top), side, tail: Math.round(tail) };
  }
  const top = side === 'bottom' ? hole.y + hole.h + GAP : hole.y - GAP - bulb.h;
  const left = clamp(along(bulb.w, hole.x, hole.w, align), MARGIN, viewport.w - MARGIN - bulb.w);
  const tail = clamp(hole.x + hole.w / 2 - left - 8, 14, bulb.w - 30);
  return { left: Math.round(left), top: Math.round(top), side, tail: Math.round(tail) };
}
